import { useEffect, useState } from 'react'
import { categoryLabel } from '../api'
import type { AdminSport } from './api'
import { createAdminSport, LOGO_ACCEPT, LOGO_MAX_BYTES, uploadSportLogo } from './api'

/**
 * "+ NEW" on Distribution › SPORTS (admin+). Creates the catalog row through
 * the create_sport RPC, then — if a logo was chosen — uploads it to Supabase
 * Storage via POST /admin/sports/:id/logo (the sport twin of the operator
 * dialog's flow). A failed upload keeps the dialog open with the error: the
 * row already exists, so RETRY UPLOAD re-sends only the file and CONTINUE
 * WITHOUT LOGO moves on (the logo can be set later on the /affiliate page).
 */

// The four canonical category strings (migration 0011). Displayed with the
// short brutalist labels via categoryLabel(), submitted as the raw values the
// sports.category check-constraint expects.
const CATEGORIES: AdminSport['category'][] = [
  'Motor Sports',
  'Combat Sports',
  'Action Sports',
  'Alternative Sports',
]

interface Props {
  onClose: () => void
  // Fired once the row exists (logo may or may not have uploaded) with the
  // new row's id so the parent can refetch the list and select it.
  onCreated: (id: number) => void
}

export default function NewSportDialog({ onClose, onCreated }: Props) {
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [category, setCategory] = useState<AdminSport['category']>('Motor Sports')
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Set once the row is created so a logo-upload failure never creates a
  // second row on retry.
  const [createdId, setCreatedId] = useState<number | null>(null)

  useEffect(() => {
    if (!logoFile) {
      setPreview(null)
      return
    }
    const url = URL.createObjectURL(logoFile)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [logoFile])

  function pickFile(f: File | null) {
    setError(null)
    if (f && f.size > LOGO_MAX_BYTES) {
      setError(`"${f.name}" is ${(f.size / 1024 / 1024).toFixed(2)} MB — the limit is 2 MB`)
      setLogoFile(null)
      return
    }
    setLogoFile(f)
  }

  const canSubmit = name.trim().length > 0 && !busy

  async function submit() {
    if (!canSubmit) return
    setBusy(true)
    setError(null)
    try {
      let id = createdId
      if (id == null) {
        const created = await createAdminSport({
          name: name.trim(),
          slug: slug.trim() === '' ? null : slug.trim(),
          category,
        })
        id = created.id
        setCreatedId(id)
      }
      if (logoFile) {
        await uploadSportLogo(id, logoFile)
      }
      onCreated(id)
    }
    catch (e) {
      setError((e as Error).message)
      setBusy(false)
    }
  }

  return (
    <div
      className="ad-modal-backdrop"
      onMouseDown={(e) => {
        // Click on the backdrop (not the panel) dismisses — not mid-request.
        if (e.target === e.currentTarget && !busy) onClose()
      }}
    >
      <div className="ad-modal" role="dialog" aria-modal="true" aria-label="Create new sport">
        <header className="ad-modal-head">
          <h2 className="b-h2">NEW SPORT</h2>
          <button type="button" className="b-btn-ghost" onClick={onClose} aria-label="Close">✕</button>
        </header>

        {error && <p className="ad-msg b-mono is-err">{error}</p>}

        <label className="ad-field">
          <span className="b-mono-dim b-tiny">NAME</span>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            className="ad-input"
            placeholder="e.g. World Rallycross"
            autoFocus
            spellCheck={false}
            onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
          />
        </label>

        <label className="ad-field">
          <span className="b-mono-dim b-tiny">CATEGORY</span>
          <select
            value={category}
            onChange={e => setCategory(e.target.value as AdminSport['category'])}
            className="ad-input"
          >
            {CATEGORIES.map(c => (
              <option key={c} value={c}>{categoryLabel(c)}</option>
            ))}
          </select>
        </label>

        <label className="ad-field">
          <span className="b-mono-dim b-tiny">SLUG — leave blank if no widget config exists</span>
          <input
            value={slug}
            onChange={e => setSlug(e.target.value)}
            className="ad-input"
            placeholder="optional"
            spellCheck={false}
            onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
          />
        </label>

        <div className="ad-field">
          <span className="b-mono-dim b-tiny">LOGO — png / jpeg / webp / svg / gif, ≤ 2 MB (optional)</span>
          <div className="ad-file-row">
            <span className="ad-tile-preview" style={{ background: 'transparent' }} aria-hidden="true">
              {preview
                ? <img src={preview} alt="" />
                : <span className="b-mono-dim b-tiny">{(name.trim() || '??').slice(0, 2).toUpperCase()}</span>}
            </span>
            <label className="b-btn b-btn-ghost" style={{ cursor: busy ? 'default' : 'pointer' }}>
              {logoFile ? 'CHANGE FILE…' : 'CHOOSE FILE…'}
              <input
                type="file"
                className="ad-file-input"
                accept={LOGO_ACCEPT}
                disabled={busy || createdId != null && !error}
                onChange={e => pickFile(e.target.files?.[0] ?? null)}
              />
            </label>
            {logoFile && (
              <>
                <span className="ad-file-name">{logoFile.name}</span>
                <button type="button" className="b-btn-ghost b-tiny" onClick={() => pickFile(null)} disabled={busy}>REMOVE</button>
              </>
            )}
          </div>
        </div>

        <p className="b-mono-dim b-tiny ad-modal-hint">
          Approvals, carriers, brand colors and affiliate URLs are set afterwards in the editors.
        </p>

        <div className="ad-modal-actions">
          <button type="button" className="b-btn-ghost" onClick={onClose} disabled={busy}>CANCEL</button>
          {createdId != null && error
            ? (
                <button type="button" className="b-btn b-btn-primary" onClick={() => onCreated(createdId)} disabled={busy}>
                  CONTINUE WITHOUT LOGO
                </button>
              )
            : null}
          <button type="button" className="b-btn b-btn-primary" onClick={submit} disabled={!canSubmit}>
            {busy ? (createdId != null ? 'UPLOADING…' : 'CREATING…') : createdId != null ? 'RETRY UPLOAD' : 'CREATE SPORT'}
          </button>
        </div>
      </div>
    </div>
  )
}
